import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import useEstouMontado from '../../hooks/useEstouMontado';
import { linhaDoProduto } from '../../utils/linhaDoProduto';

const ClientSearch = ({
  onClientSelect,
  selectedClient,
  placeholder = "Digite código (ex: 1405), nome ou CNPJ...",
  hasError = false,
  clientGroups = [],
  onGroupSelect,
  // Compacto no formulario de chamado, onde o campo serve so para
  // pesquisar. As demais telas continuam com o tamanho de sempre.
  size = 'medium',
}) => {
  const { theme } = React.useContext(ThemeContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Fechar a tela com a busca no ar deixava "state update on an unmounted
  // component" no console -- e o motivo estava no debounce, abaixo.
  const estouMontado = useEstouMontado();

  /*
   * Debounce para evitar muitas requisições.
   *
   * Isto era uma função guardada num `useCallback` com um `setTimeout` lá
   * dentro, e NINGUÉM cancelava esse timer: quem digitava e fechava a tela
   * dentro dos 300 ms disparava uma busca no servidor para uma tela que não
   * existia mais, e ela voltava gravando estado. Agora o timer é do efeito,
   * e o efeito cancela o que agendou -- é a limpeza que faltava.
   */
  useEffect(() => {
    const relogio = setTimeout(() => {
      // Garantir que o termo seja string antes de usar trim
      const termo = String(searchTerm || '').trim();
      if (termo.length >= 2) {
        performSearch(termo);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(relogio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const performSearch = async (term) => {
    setLoading(true);
    setError(null);

    try {
      // Converter para string para garantir compatibilidade
      const searchTerm = String(term).trim();

      const response = await http.get(`/client-search?q=${encodeURIComponent(searchTerm)}`);

      if (!estouMontado()) return;

      if (response.data.success) {
        setResults(response.data.data || []);
        setShowResults(true);
      } else {
        setError(response.data.message || 'Erro ao buscar clientes');
      }
    } catch (err) {
      if (estouMontado()) setError('Erro ao conectar com o servidor');
      console.error('Erro na busca de clientes:', err);
    } finally {
      if (estouMontado()) setLoading(false);
    }
  };

  const handleClientSelect = async (client, company) => {
    let updatedCompany = company;
    
    if (company.code) {
      try {
        setLoading(true);
        const response = await http.get(`/companies?code=${company.code}`);
        
        if (Array.isArray(response.data) && response.data.length > 0) {
          updatedCompany = response.data[0];
        } else if (response.data && !Array.isArray(response.data)) {
          updatedCompany = response.data;
        }
        
        if (!updatedCompany.clients || updatedCompany.clients.length === 0) {
          if (updatedCompany.id) {
            const clientsResponse = await http.get(`/client-search/company/${updatedCompany.id}`);
            const payload = clientsResponse.data;

            // API retorna { success, data: { company, clients } }
            if (payload?.success && payload.data) {
              const d = payload.data;
              if (Array.isArray(d.clients)) {
                updatedCompany.clients = d.clients;
              } else if (Array.isArray(d) && d[0]?.clients) {
                updatedCompany.clients = d[0].clients;
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados atualizados da company:', error);
      } finally {
        setLoading(false);
      }
    }
    
    const clientData = {
      ...client,
      company: updatedCompany
    };
    
    if (onClientSelect) {
      onClientSelect(clientData);
    }
    
    setShowResults(false);
    // Manter o código no campo de busca ao invés da razão social
    // Isso permite que buscas subsequentes usem o código (busca por código)
    // Se não houver código, usar razão social como fallback
    const codeToSet = updatedCompany.code || updatedCompany.business_name || updatedCompany.name || '';
    setSearchTerm(String(codeToSet));
  };

  const handleRefresh = () => {
    if (searchTerm.trim().length >= 2) {
      performSearch(searchTerm);
    }
  };

  const renderCompanyResult = (result) => {
    const { company, clients, source } = result;
    
    const activeStatus = company.active;
    const isActive = activeStatus === 'S' || (activeStatus === null && company.status === 1);
    
    const defaultClient = clients ? clients.find(client => 
      client.is_default || 
      (client.name && client.name.includes('(Cliente Padrão)'))
    ) : null;
    
    const realClients = clients ? clients.filter(client =>
      !client.is_default &&
      (!client.name || !client.name.includes('(Cliente Padrão)'))
    ) : [];

    // Grupos aos quais este registro pertence (casa o codigo da empresa com os
    // codigos de cada grupo). Permite aplicar o grupo direto na busca.
    const companyCode = String(company.code || '').trim();
    const groupsForCompany = companyCode
      ? (clientGroups || []).filter(
          (g) => Array.isArray(g.codes) && g.codes.some((cd) => String(cd).trim() === companyCode)
        )
      : [];

    return (
      <Card 
        key={`company-${company.id}`} 
        sx={{ 
          mb: 1.5, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 2,
          '&:hover': { 
            boxShadow: 3,
            cursor: 'pointer'
          }
        }}
        onClick={async () => {
          if (defaultClient) {
            await handleClientSelect(defaultClient, company);
          }
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Cabeçalho com nome da empresa e indicadores */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Nome da empresa em maiúsculas e negrito */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  color: 'text.primary',
                  mb: 0.75,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {company.business_name || company.name || 'Empresa sem nome'}
              </Typography>
              
              {/* Linha de produto, Código e CNPJ */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {/*
                  O TIPO antes de escolher, e não só depois.

                  Até a identificação do cliente MAX entrar, este rótulo não
                  separava nada -- quase tudo era Sysfar. Agora a mesma busca pode
                  devolver uma drogaria do ERP e uma confecção do MAX, e o número
                  não desempata: o código 410 existe nos DOIS sistemas, apontando
                  para empresas diferentes.

                  Sem chip, a escolha certa dependeria de o atendente reconhecer o
                  nome. Com ele, a linha de produto está na frente antes do clique.
                */}
                {linhaDoProduto(company) && (
                  <Chip
                    label={linhaDoProduto(company)}
                    data-testid="chip-linha-produto"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
                {company.code && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  >
                    Código: {company.code}
                  </Typography>
                )}
                {company.code && company.cnpj && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  >
                    |
                  </Typography>
                )}
                {company.cnpj && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  >
                    CNPJ: {company.cnpj}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Indicadores no canto superior direito */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
              {/* Ponto verde de status */}
              {isActive && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    flexShrink: 0,
                    boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 1,
                        transform: 'scale(1)'
                      },
                      '50%': {
                        opacity: 0.8,
                        transform: 'scale(1.1)'
                      }
                    }
                  }}
                />
              )}
              {/* Seta de navegação moderna */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateX(4px) scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <ArrowForwardIcon 
                  sx={{ 
                    fontSize: 16,
                    fontWeight: 'bold'
                  }} 
                />
              </Box>
            </Box>
          </Box>

          {/* Seção de GRUPOS: aplica o grupo inteiro direto da busca */}
          {groupsForCompany.length > 0 && onGroupSelect && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                {groupsForCompany.length > 1 ? 'GRUPOS:' : 'GRUPO:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {groupsForCompany.map((g) => (
                  <Tooltip key={g.id} title={`Aplicar grupo "${g.name}" ao ticket`} arrow>
                    <Chip
                      label={g.name}
                      color="primary"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        onGroupSelect(g.id);
                        setShowResults(false);
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        height: 28,
                        borderRadius: 1.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          cursor: 'pointer',
                          transform: 'translateY(-1px)',
                          boxShadow: theme.shadows[2]
                        }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {/* Seção de CONTATOS com chips */}
          {realClients.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                CONTATOS:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {realClients.map((client) => (
                  <Chip
                    key={client.id}
                    label={client.name || 'Sem nome'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClientSelect(client, company);
                    }}
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.text.primary,
                      fontSize: '0.875rem',
                      height: 28,
                      borderRadius: 1.5,
                      transition: 'all 0.2s ease',
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        bgcolor: theme.palette.action.selected,
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        size={size}
        error={hasError}
        value={searchTerm || ''}
        onChange={(e) => setSearchTerm(String(e.target.value || ''))}
        placeholder={placeholder}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          endAdornment: loading && <CircularProgress size={20} />,
          inputRef: (el) => setAnchorEl(el)
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2
          }
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Popper open={Boolean(showResults)} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: (theme) => theme.zIndex.modal + 1, width: anchorEl ? anchorEl.clientWidth : undefined }} modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
        <ClickAwayListener onClickAway={() => setShowResults(false)}>
          <Paper elevation={3} sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider' }}>
            {results.length > 0 ? (
              <Box>
                <Box sx={{ p: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {results.length} {results.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                  </Typography>
                </Box>
                <Box sx={{ p: 0.5 }}>
                  {results.map(renderCompanyResult)}
                </Box>
              </Box>
            ) : (!loading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum resultado encontrado
                </Typography>
              </Box>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default ClientSearch;
